import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "sonner";
import { useLogin } from "@/features/auth/services";
import { isApiError, type ApiError } from "@/shared/api";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Button } from "@/shared/components/ui/button";
// Temporarily hidden - Google login imports
// import SignInWithGoogle from "./SignInWIthGoogle";
// import Separator from "./Separator";
import { persistAccessToken } from "@/shared/utils/auth";
import { setPhoneVerificationState } from "@/shared/utils/phoneVerification";
import { reportToSentry } from "@/shared/monitoring/sentry";
import { setPendingEmailVerification } from "../utils/email-verification";
import {
  buildEmailVerificationCodePath,
  isEmailVerificationRequiredError,
} from "../utils/email-verification-flow";

const signInSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  rememberMe: z.boolean(),
});

type SignInFormValues = z.infer<typeof signInSchema>;

const UNKNOWN_ERROR_MESSAGE =
  "Unable to sign you in. Please try again in a moment.";

const SIGN_IN_FIELD_NAMES: Array<keyof SignInFormValues> = [
  "email",
  "password",
  "rememberMe",
];

const asNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

const normalizeFieldErrorMessage = (value: unknown) => {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .filter((item): item is string => typeof item === "string")
      .join(" ");
  }

  return undefined;
};

const extractLoginServerError = (error: ApiError) => {
  let errorMessage = asNonEmptyString(error.message);
  let errorName = asNonEmptyString(error.errorName);
  let errorCode = asNonEmptyString(error.errorCode);

  if (error.data && typeof error.data === "object") {
    const payload = error.data as Record<string, unknown>;

    if (!errorMessage) {
      errorMessage =
        asNonEmptyString(payload.errorMessage) ??
        asNonEmptyString(payload.message) ??
        errorMessage;
    }

    if (!errorName) {
      errorName = asNonEmptyString(payload.errorName) ?? errorName;
    }

    if (!errorCode) {
      errorCode = asNonEmptyString(payload.errorCode) ?? errorCode;
    }
  }

  return {
    errorMessage,
    errorName,
    errorCode,
    requestId: error.requestId,
  };
};

export default function SignInForm() {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: login, isPending } = useLogin();
  const redirectTo =
    (location.state as { from?: string } | null)?.from ?? "/dashboard";

  const form = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmissionError(null);
    form.clearErrors();

    try {
      const normalizedEmail = values.email.trim();
      const response = await login({
        email: normalizedEmail,
        password: values.password,
      });

      persistAccessToken(response.accessToken, values.rememberMe);
      setPhoneVerificationState({
        verified: response.user.phoneNumberVerified === true,
        phoneNumber: response.user.phoneNumber ?? null,
      });

      if (!response.user.phoneNumber || !response.user.phoneNumberVerified) {
        toast.info("Please verify your phone number to continue.");
        const params = new URLSearchParams();
        if (response.user.phoneNumber) {
          params.set("phone", response.user.phoneNumber);
        }
        if (redirectTo) {
          params.set("redirectTo", redirectTo);
        }

        const targetPath = params.toString()
          ? `/verify-phone?${params.toString()}`
          : "/verify-phone";

        navigate(targetPath, {
          state: {
            phoneNumber: response.user.phoneNumber ?? "",
            redirectTo,
          },
        });
        return;
      }

      toast.success(
        response.user.firstName
          ? `Welcome back, ${response.user.firstName}!`
          : "Welcome back!"
      );
      navigate(redirectTo, { replace: true });
    } catch (error) {
      if (isApiError(error)) {
        const { errorMessage, errorName, errorCode, requestId } =
          extractLoginServerError(error);
        const message = errorMessage ?? UNKNOWN_ERROR_MESSAGE;
        toast.error(message, {
          description: requestId ? `Request ID: ${requestId}` : undefined,
        });
        setSubmissionError(message);

        if (error.errors) {
          for (const [field, fieldError] of Object.entries(error.errors)) {
            if (SIGN_IN_FIELD_NAMES.includes(field as keyof SignInFormValues)) {
              const fieldMessage = normalizeFieldErrorMessage(fieldError);
              if (fieldMessage) {
                form.setError(field as keyof SignInFormValues, {
                  type: "server",
                  message: fieldMessage,
                });
              }
            }
          }
        }

        if (
          isEmailVerificationRequiredError({
            errorMessage: message,
            errorName,
            errorCode,
          })
        ) {
          const normalizedEmail = values.email.trim();
          toast.info(
            "Email verification required. Please verify your email to continue."
          );
          setPendingEmailVerification(normalizedEmail, {
            redirectPath: redirectTo,
            source: "login",
          });

          navigate(
            buildEmailVerificationCodePath({
              email: normalizedEmail,
              redirectPath: redirectTo,
            }),
            {
              replace: true,
              state: {
                email: normalizedEmail,
                redirectPath: redirectTo,
                source: "login",
              },
            }
          );

          return;
        }

        if (
          errorName === "invalid-credentials" ||
          errorCode === "400201" ||
          errorCode === "401000"
        ) {
          form.setError("password", {
            type: "server",
            message,
          });
          form.setFocus("password");
          return;
        }

        return;
      }

      reportToSentry(error, {
        mechanism: "app",
        handled: true,
        tags: {
          feature: "login",
        },
      });
      toast.error(UNKNOWN_ERROR_MESSAGE);
      setSubmissionError(UNKNOWN_ERROR_MESSAGE);
    }
  });

  return (
    <div className="my-6 flex w-full max-w-sm flex-col gap-4 sm:my-8 sm:max-w-xs">
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          {/* Temporarily hidden - Google login */}
          {/* <SignInWithGoogle /> */}
          {/* <Separator /> */}
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    inputMode="email"
                    placeholder="name@email.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-label-dark">Password</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex flex-col gap-3 text-sm min-[360px]:flex-row min-[360px]:items-center min-[360px]:justify-between">
            <FormField
              control={form.control}
              name="rememberMe"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) =>
                          field.onChange(checked === true)
                        }
                      />
                    </FormControl>
                    <FormLabel className="text-label-dark mb-0">
                      Remember Me
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Link
              to="/forgot-password"
              className="self-start underline text-white min-[360px]:self-auto">
              Forgot Password?
            </Link>
          </div>
          <Button
            type="submit"
            variant="primary"
            disabled={isPending}
            className="h-11 w-full rounded-lg">
            {isPending ? "Signing in..." : "Sign In"}
          </Button>
          {submissionError && (
            <p className="text-danger text-sm text-center">{submissionError}</p>
          )}
        </form>
      </Form>
    </div>
  );
}
